import { useCallback, useEffect, useState } from "react";
import { Lightbulb } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { TeamProjGraphics } from "./TeamProjGraphics";
import { TeamValueTab, ValueChip } from "./TeamValue";
import { TeamBreakdown } from "./TeamBreakdown";
import { ScoreComparison } from "./ScoreComparison";
import "./ScoreComparison.css";
import { useAuth } from "@/context/AuthContext";
import "./TeamProjections.css";

const logo = (code) => `https://a.espncdn.com/i/teamlogos/nba/500/${code?.toLowerCase()}.png`;
const pct = (v) => `${Math.round(v * 100)}%`;
const dec = (v) => (v == null ? null : Number(v).toFixed(2));
const sgn = (v) => (v > 0 ? `+${v}` : `${v}`);
const time = (iso) => new Date(iso).toLocaleTimeString("en-GB", { timeZone: "Europe/Athens", hour: "2-digit", minute: "2-digit" });
const day = (iso) => new Date(iso).toLocaleDateString("en-GB", { timeZone: "Europe/Athens", day: "numeric", month: "short", year: "numeric" });

function Head({ g, t, b2b }) {
  const side = (s) => (
    <div className={`tp-team ${s}`}>
      <img src={logo(g[`${s}_code`])} alt="" />
      <div><strong><span className="tp-full">{g[`${s}_name`] || g[`${s}_code`]}</span><span className="tp-code">{g[`${s}_code`]}</span></strong>
        <small>{t[s].wins ?? 0}-{t[s].losses ?? 0}{t[s].seed ? ` · ${t[s].seed}${["", "st", "nd", "rd"][t[s].seed] || "th"} ${(t[s].conference || "").split(" ")[0]}` : ""}{b2b[s] && <em className="tp-b2b">B2B</em>}</small></div>
    </div>
  );
  return (
    <div className="tp-head" data-testid="tp-head">
      {side("away")}
      <div className="tp-when"><b data-testid="tp-when-main">{g.status === "pre" || g.away_pts == null ? time(g.date_utc) : `${g.away_pts} – ${g.home_pts}`}</b><small>{day(g.date_utc)} · {g.status === "pre" ? "Athens" : time(g.date_utc)}</small><small>{g.status === "pre" ? "Pre-game" : g.status_detail}</small></div>
      {side("home")}
    </div>
  );
}

const Box = ({ title, children, id, wide, value }) => <div className={`tp-card tp-box ${wide ? "wide" : ""} ${value ? "has-value" : ""}`} data-testid={`tp-${id}`}><h4>{title}{value && <ValueChip id={id} />}</h4>{children}</div>;
const Pair = ({ code, model, book, cls = "" }) => (
  <div className={`tp-pair ${cls}`}><span>{code}</span><b>{model}</b>{book !== undefined && <em>{book ?? "—"}</em>}</div>
);

function Lines({ g, p, picks = [] }) {
  const b = p.book || {};
  const has = (m) => picks.some((x) => x.market === m);
  return (
    <div className="tp-lines">
      <Box title="WIN PROBABILITY" id="winprob" wide>
        <div className="tp-wp"><img src={logo(g.away_code)} alt="" /><b>{pct(p.p_away)}</b><b>{pct(p.p_home)}</b><img src={logo(g.home_code)} alt="" /></div>
        <div className="tp-split"><i style={{ width: `${p.p_away * 100}%` }} /><em /></div>
      </Box>
      <Box title="MONEYLINE" id="moneyline" value={has("moneyline")}>
        <p className="tp-colhead"><span /><small>MODEL</small><small>{b.provider || "BOOK"}</small></p>
        <Pair code={g.away_code} model={dec(p.fair_ml_away)} book={dec(b.ml_away)} />
        <Pair code={g.home_code} model={dec(p.fair_ml_home)} book={dec(b.ml_home)} />
      </Box>
      <Box title="SPREAD" id="spread" value={has("spread")}>
        <p className="tp-colhead"><span /><small>MODEL</small><small>{b.provider || "BOOK"}</small></p>
        <Pair code={g.away_code} model={sgn(-p.spread_home)} book={b.spread_home != null ? sgn(-b.spread_home) : null} />
        <Pair code={g.home_code} model={sgn(p.spread_home)} book={b.spread_home != null ? sgn(b.spread_home) : null} />
      </Box>
      <Box title="GAME TOTAL" id="total" value={has("total")}>
        <div className="tp-total"><div><small>MODEL</small><b>{p.total}</b></div><div><small>{b.provider || "LINE"}</small><b>{b.total ?? "—"}</b></div></div>
        {p.p_over != null && <p className="tp-mini">Over {b.total}: {pct(p.p_over)} · Under: {pct(1 - p.p_over)}</p>}
      </Box>
      <Box title="TEAM TOTALS" id="teamtotals">
        <Pair code={g.away_code} model={p.away} />
        <Pair code={g.home_code} model={p.home} cls="hl" />
      </Box>
    </div>
  );
}

function Form({ g, form }) {
  return (
    <div className="tp-card" data-testid="tp-form">
      <h4>RECENT FORM <small>(Last 5)</small></h4>
      {["away", "home"].map((s) => (
        <div key={s} className="tp-form-row"><img src={logo(g[`${s}_code`])} alt="" /><span>{g[`${s}_code`]}</span>
          <div>{form[s].map((f, i) => <i key={i} className={f.win ? "w" : "l"} title={`${f.win ? "W" : "L"} vs ${f.opp} ${f.score}`}>{f.win ? "W" : "L"}</i>)}
            {!form[s].length && <small className="tp-muted">No games yet</small>}</div>
        </div>
      ))}
    </div>
  );
}

function Metrics({ g, d, scope = "season" }) {
  const p = d.projection;
  const c = d.comparison[scope];
  const rows = [["Projected points", p.away, p.home], ["Points per game", d.teams.away.ppg, d.teams.home.ppg],
    ["Points allowed", d.teams.away.opp_ppg, d.teams.home.opp_ppg], ["Point diff", d.teams.away.diff, d.teams.home.diff],
    ["Rebounds", c.away.rpg, c.home.rpg], ["Assists", c.away.apg, c.home.apg], ["3PT %", c.away.fg3_pct, c.home.fg3_pct]];
  return (
    <div className="tp-card" data-testid="tp-metrics">
      <h4>KEY TEAM METRICS <small>(season)</small></h4>
      <table className="tp-table"><thead><tr><th>METRIC</th><th>{g.away_code}</th><th>{g.home_code}</th></tr></thead>
        <tbody>{rows.map(([k, a, h]) => {
          const low = k === "Points allowed";  // lower is better
          const best = a == null || h == null || a === h ? null : (a > h) !== low ? "a" : "h";
          return <tr key={k}><td>{k}</td><td className={best === "a" ? "hi" : ""}>{a ?? "—"}</td><td className={best === "h" ? "hi" : ""}>{h ?? "—"}</td></tr>;
        })}</tbody>
      </table>
    </div>
  );
}

function Angle({ p }) {
  const conf = Math.max(p.p_home, p.p_away);
  return (
    <div className="tp-card tp-angle" data-testid="tp-angle">
      <span className="tp-bulb"><Lightbulb size={22} /></span>
      <div><h4>BETTING ANGLE</h4><p data-testid="tp-angle-text">{p.angle}</p></div>
      <div className="tp-conf"><small>Model confidence: <b>{pct(conf)}</b></small><div className="tp-confbar"><i style={{ width: pct(conf) }} /></div></div>
    </div>
  );
}

function Score({ g, p }) {
  return (
    <div className="tp-card tp-score" data-testid="tp-projected-score">
      <h4>PROJECTED SCORE</h4>
      <div className="tp-score-row"><div><img src={logo(g.away_code)} alt="" /><small>{g.away_code}</small></div>
        <b data-testid="tp-score-value">{Math.round(p.away)} - {Math.round(p.home)}</b>
        <div><img src={logo(g.home_code)} alt="" /><small>{g.home_code}</small></div></div>
      <div className="tp-split"><i style={{ width: `${(p.away / p.total) * 100}%` }} /><em /></div>
    </div>
  );
}

function TeamStats({ g, d }) {
  const [scope, setScope] = useState("season");
  const c = d.comparison[scope];
  const rows = [["Points", "ppg"], ["Rebounds", "rpg"], ["Assists", "apg"], ["3PT %", "fg3_pct"]];
  return (
    <div className="tp-card" data-testid="tp-teamstats">
      <div className="tp-subtabs">{[["season", "Season"], ["last5", "Last 5"]].map(([k, t]) => <button key={k} type="button" className={scope === k ? "is-active" : ""} onClick={() => setScope(k)} data-testid={`tp-stats-${k}`}>{t}</button>)}</div>
      {rows.map(([label, k]) => {
        const a = c.away[k] ?? 0; const h = c.home[k] ?? 0; const m = Math.max(a, h) || 1;
        return (
          <div key={k} className="tp-cmp"><b>{c.away[k] ?? "—"}</b><div className="l"><i style={{ width: `${(a / m) * 100}%` }} /></div>
            <span>{label}</span><div className="r"><i style={{ width: `${(h / m) * 100}%` }} /></div><b>{c.home[k] ?? "—"}</b></div>
        );
      })}
      <p className="tp-note">{g.away_code} {c.away.games} games · {g.home_code} {c.home.games} games, from NBACUBE box scores{scope === "last5" ? " (last 5 can include last season's games early on)" : ""}.</p>
    </div>
  );
}

function Insights({ g, d }) {
  const p = d.projection;
  const b = p.book || {};
  const items = p.angle.replace(/\.$/, "").split("; ");
  return (
    <div className="tp-card" data-testid="tp-insights">
      <h4>BETTING INSIGHTS</h4>
      <ul className="tp-list">{items.map((t) => <li key={t}>{t}</li>)}</ul>
      <table className="tp-table"><thead><tr><th>MARKET</th><th>MODEL</th><th>{b.provider || "BOOK"}</th><th>MODEL CHANCE</th></tr></thead>
        <tbody>
          <tr><td>{g.home_code} spread</td><td>{sgn(p.spread_home)}</td><td>{b.spread_home != null ? sgn(b.spread_home) : "—"}</td><td>{p.p_home_cover != null ? `${pct(p.p_home_cover)} cover` : "—"}</td></tr>
          <tr><td>Total</td><td>{p.total}</td><td>{b.total ?? "—"}</td><td>{p.p_over != null ? `${pct(p.p_over)} over` : "—"}</td></tr>
          <tr><td>{g.home_code} moneyline</td><td>{dec(p.fair_ml_home)}</td><td>{dec(b.ml_home) ?? "—"}</td><td>{pct(p.p_home)} win</td></tr>
        </tbody>
      </table>
      <p className="tp-note">Model v1: team scoring averages (own points + opponent&apos;s points allowed) with home-court edge. Bookmaker lines are shown separately and do not feed the model.</p>
    </div>
  );
}

const TABS = [["lines", "GAME LINES"], ["value", "VALUE PICKS"], ["stats", "TEAM STATS"], ["insights", "BETTING INSIGHTS"]];

export function TeamProjectionsPanel({ game, day, onSelect }) {
  const isAdmin = useAuth().user?.role === "admin";
  const [tab, setTab] = useState("lines");
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    if (!game?.id) return;
    api.get(`/team-projections/${game.id}`).then((r) => setD(r.data)).catch((e) => setError(errorText(e)));
  }, [game?.id]);
  useEffect(() => { setD(null); setError(""); load(); }, [load]);
  return (
    <section className="tv-panel tp-root" aria-label="Team projections" data-testid="tp-panel">
      <header className="tv-panel-head tp-top">
        <h2>TEAM PROJECTIONS</h2>
        <div className="tv-pills tv-mode">{TABS.map(([k, t]) => <button key={k} type="button" className={tab === k ? "is-active" : ""} onClick={() => setTab(k)} data-testid={`tp-tab-${k}`}>{t}</button>)}</div>
      </header>
      <div className="tv-scroll tp-body">
        {!game && <p className="tv-empty">Select a game.</p>}
        {error && <p className="tv-empty" data-testid="tp-error">{error}</p>}
        {game && !d && !error && <p className="tv-empty">Loading…</p>}
        {d && <Head g={d.game} t={d.teams} b2b={d.b2b} />}
        {d && !d.projection && <p className="tv-empty" data-testid="tp-noproj">Not enough games yet for a team projection.</p>}
        {d?.projection && tab === "lines" && (
          <>
            <Lines g={d.game} p={d.projection} picks={d.value?.picks} />
            <div className="tp-grid2">
              <div className="tp-col"><Score g={d.game} p={d.projection} /><Form g={d.game} form={d.form} /></div>
              <Metrics g={d.game} d={d} />
            </div>
            <Angle p={d.projection} />
            {isAdmin && <ScoreComparison game={{ id: d.game.id, away_code: d.game.away_code, home_code: d.game.home_code, status: d.game.status }} />}
            <TeamProjGraphics g={d.game} p={d.projection} />
            {isAdmin && <TeamBreakdown game={{ id: d.game.id, away_code: d.game.away_code, home_code: d.game.home_code, day: d.game.game_day }} />}
          </>
        )}
        {d?.projection && tab === "value" && <TeamValueTab game={d.game} value={d.value} book={d.projection.book} day={day || d.game.game_day} onSelect={onSelect} reload={load} />}
        {d?.projection && tab === "stats" && <TeamStats g={d.game} d={d} />}
        {d?.projection && tab === "insights" && <Insights g={d.game} d={d} />}
      </div>
    </section>
  );
}
